import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
	checkIfProductHasReferralReason,
	formatCurrency,
	MAX_DATE,
	MAX_FORWARD_DAYS,
	MAX_POLICY_PERIOD,
	MIN_DATE,
	MIN_POLICY_PERIOD,
	regexGSOF,
} from '../../helper/constants';
import { regions } from '../../helper/regions';
import { StatusNumber } from '../../helper/statusNumber';
import {
	DecisionTree,
	DecisionTreeOption,
	DecisionTreeOrQuoteProduct,
	Quote,
	QuoteGroupResponse,
	QuoteReferralReason,
	QuoteTotals,
    ReferralReasonDisplay,
} from '../types/quotes';
import { addDays, isAfter } from 'date-fns';
import { initialRegionData, RegionData } from '../types/regionData';
import { useGetQuoteGroupQuery } from '../../services/features/quoteApi/quoteApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { DecisionTreeOptionAction, QuestionStatus } from '../../helper/questionStatus';

// The context type
interface QuoteContextType {
	decisionTree: DecisionTreeOrQuoteProduct[];
	splitDecisionTree: DecisionTreeOrQuoteProduct[];
	quoteGroup: QuoteGroupResponse | undefined;
	currentQuotes: Quote[];
	policyIds: number[];
	needsReferral: boolean;
	hasReferralReason: boolean;
	willBeDeclined: boolean;
	isDeclined: boolean;
	isReferred: boolean;
	isQuotePolicyCreated: boolean;
	isReferralApproved: boolean;
	isReferralApprovedModifiable: boolean;
	isQuoteInProgress: boolean;
	isEveryQuoteInProgress: boolean;
	isQuoteBound: boolean;
	currentPremium: number;
	bindButtonEnabled: boolean;
	hasSanctions: boolean;
	allReferralReasons: ReferralReasonDisplay[];
	calculateTotalsAndPercentages: (updatedRows: RegionData[]) => RegionData[];
	mapQuotesToRows: (quotes: Quote[]) => void;
	setDecisionTree: React.Dispatch<React.SetStateAction<DecisionTreeOrQuoteProduct[]>>;
	// setCurrentQuotes: React.Dispatch<React.SetStateAction<Quote[]>>;
	isQuoteDisabled: (quoteId: number) => boolean;
	feeBreakdownRows: FeeBreakdownRow[];
	loadingProducts: boolean;
	setLoadingProducts: React.Dispatch<React.SetStateAction<boolean>>;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	ratingRegionsRows: RegionData[];
	setRatingRegionsRows: React.Dispatch<React.SetStateAction<RegionData[]>>;
	quoteCheckbox: any[];
	setQuoteCheckbox: React.Dispatch<React.SetStateAction<any[]>>;
	minPolicyPeriod: number;
	maxPolicyPeriod: number;
	maxForwardDays: number;
	backdatingDays: number;
	minDate: Date;
	minBackdatedDate: Date;
	maxDate: Date;
}

interface FeeBreakdownRow {
	id: number;
	package: string;
	gst: string;
	adminFee: string;
	adminFeeGST: string;
	basePremium: string;
	// commission: string;
	// gstOnCommission: string;
	stampDuty: string;
	totalPremium: string;
	// nettPayable: string;
}

// Initial context value
const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

const QuoteProvider = ({ children }: any) => {
	const { quoteId, quoteGroup } = useSelector((state: RootState) => state.quoteSlice);

	// const { data: quoteGroup, ...quoteQuery } = useGetQuoteGroupQuery(quoteId ?? skipToken, {
	const quoteQuery = useGetQuoteGroupQuery(quoteId ?? skipToken, {
		skip: !quoteId,
	});

	const [quoteCheckbox, setQuoteCheckbox] = useState<any[]>([]);

	const currentQuotes = useMemo(() => {
		// console.log('CURRENT QUOTES UPDATED', quoteGroup?.quotes);
		return quoteGroup?.quotes ?? [];
	}, [quoteGroup]); //, quoteQuery.fulfilledTimeStamp]);

	useEffect(() => {
		if ((quoteGroup?.quotes ?? []).length > 0) {
			setQuoteCheckbox([]);
		}
	}, [quoteGroup?.quotes]);

	const policyIds = useMemo(
		() => (currentQuotes?.map((q) => q.policyId) ?? []).filter((id) => id !== 0),
		[currentQuotes]
	);

	const [decisionTree, setDecisionTree] = useState<DecisionTreeOrQuoteProduct[]>([]);
	// const [currentQuotes, setCurrentQuotes] = useState<Quote[]>([]);

	const [ratingRegionsRows, setRatingRegionsRows] = useState<RegionData[]>(
		initialRegionData.slice()
	);

	const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const isQuoteLoading = quoteQuery.isLoading || quoteQuery.isFetching;

	// Quote statuses

	const hasSanctions = useMemo(
		() => (quoteGroup?.quoteClientSanctions?.numOfResults ?? 0) > 0,
		[quoteGroup?.quoteClientSanctions?.numOfResults]
	);

	const hasReferralReason = useMemo(
		() =>
			(quoteGroup?.referralReasons ?? []).length > 0 ||
			currentQuotes.some(
				(quote) =>
					quote.quoteProducts.some((prod) => checkIfProductHasReferralReason(prod)) ||
					quote.quotePackages.some(
						(pack) =>
							pack.referralReasons.length > 0 ||
							pack.quoteProducts.some((prod) => checkIfProductHasReferralReason(prod))
					)
			),
		[currentQuotes, quoteGroup?.referralReasons]
	);

	const allReferralReasons = useMemo(() => {
		const reasons = (quoteGroup?.referralReasons ?? []).map((reason) => ({
			type: 'client',
			...reason,
		}));
		const productReasons = currentQuotes
			.flatMap((quote) =>
				quote.quoteProducts.filter((prod) => checkIfProductHasReferralReason(prod))
			)
			.flatMap((prod) =>
				prod.referralReasons.map((reason) => ({
					type: 'product',
					productId: prod.id,
					productName: prod.name,
					...reason,
				}))
			);
		const packageReasons = currentQuotes
			.flatMap((quote) =>
				quote.quotePackages.filter(
					(pack) =>
						pack.referralReasons.length > 0 ||
						pack.quoteProducts.filter((prod) => checkIfProductHasReferralReason(prod))
				)
			)
			.flatMap((pack) => [
				...pack.referralReasons.map((reason) => ({
					type: 'package',
					packageId: pack.id,
					packageName: pack.name,
					...reason,
				})),
				...pack.quoteProducts
					.filter((prod) => checkIfProductHasReferralReason(prod))
					.flatMap((prod) =>
						prod.referralReasons.map((reason) => ({
							type: 'package',
							packageId: pack.id,
							packageName: pack.name,
							productId: prod.id,
							productName: prod.name,
							...reason,
						}))
					),
			]);
		const allReasons = [...reasons, ...productReasons, ...packageReasons];
		const groupedReferralReasons = allReasons.reduce<Record<string, ReferralReasonDisplay[]>>(
			(acc, reason) => {
				const { type } = reason;
				if (!acc[type]) {
					acc[type] = [];
				}
				acc[type].push(reason);
				return acc;
			},
			{}
		);
		console.log('REFERRAL REASONS', groupedReferralReasons);
		return allReasons;
	}, [currentQuotes, quoteGroup?.referralReasons]);

	const isReferred = useMemo(
		() => currentQuotes.some((quote) => quote.status.statusNumber === StatusNumber.Referred),
		[currentQuotes]
	);

	const isDeclined = useMemo(
		() => currentQuotes.some((quote) => quote.status.statusNumber === StatusNumber.Declined),
		[currentQuotes]
	);

	const isQuotePolicyCreated = useMemo(
		() =>
			currentQuotes.some(
				(quote) => quote.status.statusNumber !== StatusNumber.QuotePolicyCreated
			),
		[currentQuotes]
	);

	const isReferralApproved = useMemo(
		() =>
			currentQuotes.some(
				(quote) => quote.status.statusNumber === StatusNumber.ReferralApproved
			),
		[currentQuotes]
	);

	const isReferralApprovedModifiable = useMemo(
		() =>
			currentQuotes.some(
				(quote) => quote.status.statusNumber === StatusNumber.ReferralApprovedModifiable
			),
		[currentQuotes]
	);

	const isQuoteInProgress = useMemo(
		() =>
			currentQuotes.length === 0 ||
			currentQuotes.some(
				(quote) => quote.status.statusNumber === StatusNumber.QuoteInProgress
			),
		[currentQuotes]
	);

	const isEveryQuoteInProgress = useMemo(
		() =>
			currentQuotes.every(
				(quote) => quote.status?.statusNumber === StatusNumber.QuoteInProgress
			),
		[currentQuotes]
	);

	const isQuoteBound = useMemo(
		() =>
			currentQuotes.some(
				(quote) => quote.status?.statusNumber === StatusNumber.QuotePolicyCreated
			),
		[currentQuotes]
	);

	const minPolicyPeriod = useMemo(() => {
		const dates = (quoteGroup?.quotes ?? [])
			.filter((q) => typeof q.policyPeriodEndDateMinMonths === 'number')
			.map((q) => q.policyPeriodEndDateMinMonths) as number[];
		return dates.length > 0 ? Math.min(...dates) : MIN_POLICY_PERIOD;
	}, [quoteGroup]);

	const maxPolicyPeriod = useMemo(() => {
		const dates = (quoteGroup?.quotes ?? [])
			.filter((q) => typeof q.policyPeriodEndDateMaxMonths === 'number')
			.map((q) => q.policyPeriodEndDateMaxMonths) as number[];
		return dates.length > 0 ? Math.min(...dates) : MAX_POLICY_PERIOD;
	}, [quoteGroup]);

	const maxForwardDays = useMemo(() => {
		const dates = (quoteGroup?.quotes ?? [])
			.filter((q) => typeof q.policyPeriodStartDateFutureDatingDays === 'number')
			.map((q) => q.policyPeriodStartDateFutureDatingDays) as number[];
		return dates.length > 0 ? Math.min(...dates) : MAX_FORWARD_DAYS;
	}, [quoteGroup]);

	const backdatingDays = useMemo(() => {
		const days = (quoteGroup?.quotes ?? [])
			.filter((q) => typeof q.policyPeriodStartDateBackdatingDays === 'number')
			.map((q) =>
				Math.abs((q.policyPeriodStartDateBackdatingDays ?? 0) as number)
			) as number[];
		return days.length > 0 ? Math.min(...days) : 0;
	}, [quoteGroup]);

	const minDate = useMemo(() => {
		// const dates = (quoteGroup?.quotes ?? [])
		// 	.map((q) => (q.lastModifiedDate ? new Date(q.lastModifiedDate) : null))
		// 	.filter((d) => d != null) as Date[];
		// dates.sort((a, b) => a.getTime() - b.getTime());
		// return dates.length > 0 ? dates[dates.length - 1] : new Date(MIN_DATE);
		return new Date(MIN_DATE);
	}, []);
	// }, [quoteGroup]);

	const minBackdatedDate = useMemo(() => {
		// const dates = (quoteGroup?.quotes ?? [])
		// 	.map((q) => (q.lastModifiedDate ? new Date(q.lastModifiedDate) : null))
		// 	.filter((d) => d != null) as Date[];
		// dates.sort((a, b) => a.getTime() - b.getTime());
		// const date = dates.length > 0 ? dates[dates.length - 1] : new Date(MIN_DATE);
		return addDays(minDate, backdatingDays > 0 ? -backdatingDays : 0);
	}, [backdatingDays, minDate]);

	const maxDate = useMemo(() => {
		// const dates = (quoteGroup?.quotes ?? [])
		// 	.map((q) => (q.lastModifiedDate ? new Date(q.lastModifiedDate) : null))
		// 	.filter((d) => d != null) as Date[];
		// dates.sort((a, b) => a.getTime() - b.getTime());
		const minimumDate = minDate; //dates.length > 0 && isReferralApproved ? dates[dates.length - 1] : minDate;
		// const min = new Date(MIN_DATE);
		const date = addDays(minimumDate, maxForwardDays);
		date.setHours(23, 59, 59, 999); // Set MAX_DATE to very end of the day
		return date;
	}, [maxForwardDays, minDate]);
	// }, [quoteGroup, maxForwardDays, minDate, isReferralApproved]);

	const getSelectedOptions = (
		options: DecisionTreeOption[] | undefined
	): DecisionTreeOption[] => {
		if (!options || options.length === 0) return [];

		return options.flatMap((option) => {
			// Include current option if selected
			const current = option.selected ? [option] : [];

			// Recursively gather selected options from nested questions
			const nestedOptions = option.decisionTreeOptionQuestion
				? getSelectedOptions(option.decisionTreeOptionQuestion.decisionTreeOptions)
				: [];

			// Combine current and nested options
			return [...current, ...nestedOptions];
		});
	};

	// Question statuses

	const needsReferral = useMemo(
		() =>
			hasSanctions ||
			hasReferralReason ||
			decisionTree.some((dt) => {
				const questionSet =
					'decisionTreeQuestions' in dt
						? dt.decisionTreeQuestions
						: dt.quoteProductDecisionTree?.decisionTreeQuestions;

				const flatOptions =
					questionSet?.flatMap((q) => getSelectedOptions(q.decisionTreeOptions)) ?? [];

				const hasReferral = flatOptions.some(
					(o) => (o.decisionTreeOptionAction?.name ?? '').toUpperCase() === 'REFER'
				);

				const action =
					'decisionTreeAction' in dt
						? dt.decisionTreeAction
						: dt.quoteProductDecisionTree?.decisionTreeAction;

				return (action?.name ?? '').toUpperCase() === 'REFER' || hasReferral;
			}),
		[decisionTree, hasSanctions, hasReferralReason]
	);

	const willBeDeclined = useMemo(
		() =>
			decisionTree.some((dt) => {
				const questionSet =
					'decisionTreeQuestions' in dt
						? dt.decisionTreeQuestions
						: dt.quoteProductDecisionTree?.decisionTreeQuestions;

				const flatOptions =
					questionSet?.flatMap((q) => getSelectedOptions(q.decisionTreeOptions)) ?? [];
				// const flatOptions = questionSet
				// 	?.flatMap((q) => q.decisionTreeOptions)
				// 	.filter((o) => o.selected);
				const hasDecline = flatOptions?.some(
					(o) => (o.decisionTreeOptionAction?.name ?? '').toUpperCase() === 'DECLINE'
				);
				const action =
					'decisionTreeAction' in dt
						? dt.decisionTreeAction
						: dt.quoteProductDecisionTree?.decisionTreeAction;
				return (action?.name ?? '').toUpperCase() === 'DECLINE' || hasDecline;
			}),
		[decisionTree]
	);

	// Current premium
	const currentPremium = useMemo(() => {
		const quotes = currentQuotes.filter(
			(quote) =>
				// quote.status?.statusNumber !== StatusNumber.Referred &&
				quote.status?.statusNumber !== StatusNumber.Declined
		);
		return quotes
			.flatMap((quote) => quote.totals)
			.reduce((prev, current) => prev + current.basePremium, 0);
	}, [currentQuotes]);

	// Show bind button
	const bindButtonEnabled = isQuoteInProgress || isReferralApproved;

	const generateTotals = (quote: Quote) => {
		return {
			id: quote.id,
			package:
				quote.quotePackages.length > 0
					? quote.quotePackages[0].name
					: quote.quoteProducts[0].name,
			basePremium: formatCurrency(quote.totals.basePremium),
			gst: formatCurrency(quote.totals.basePremiumGst),
			stampDuty: formatCurrency(quote.totals.stampDuty),
			adminFee: formatCurrency(quote.totals.adminFee),
			adminFeeGST: formatCurrency(quote.totals.adminFeeGst),
			totalPremium: formatCurrency(quote.totals.totalPremium),
		};
	};

	const feeBreakdownRows = useMemo(
		() =>
			currentQuotes
				.filter((q) => q.quoteProducts.length > 0)
				.map((q) => generateTotals(q))
				.concat(
					currentQuotes
						.filter((q) => q.quotePackages.length > 0)
						.map((q) => generateTotals(q))
				),
		[currentQuotes]
	);

	const isQuoteDisabled = (quoteId: number): boolean => {
		const quote = currentQuotes.find((q) => q.id === quoteId);
		if (quote) {
			const isDisabled =
				quote.status.statusNumber === StatusNumber.Declined ||
				quote.status.statusNumber === StatusNumber.Referred;
			const isReferralApproved = quote.status.statusNumber === StatusNumber.ReferralApproved;
			const tree = quote.quoteProducts
				.concat(quote.quotePackages.flatMap((p) => p.quoteProducts))
				.flatMap((p) => p.quoteProductDecisionTree);
			const needsRefer = tree.some(
				(q) => (q?.decisionTreeAction?.name ?? '').toUpperCase() === 'REFER'
			);
			const isDecline = tree.some(
				(q) => (q?.decisionTreeAction?.name ?? '').toUpperCase() === 'DECLINE'
			);
			return isDisabled || isDecline || (!isReferralApproved && needsRefer);
		}
		return true;
	};

	const calculateTotalsAndPercentages = useCallback((updatedRows: RegionData[]) => {
		const newRows = [...updatedRows];
		const nonTotalsRows = updatedRows.filter((r) => r.name.toLowerCase() !== 'total');

		const rowIndex = updatedRows.findIndex((r) => r.name.toLowerCase() === 'total');

		if (rowIndex ?? -1 > 0) {
			newRows[rowIndex].lastYearAmount = nonTotalsRows.reduce(
				(sum, row) => sum + Number(row.lastYearAmount),
				0
			);
			newRows[rowIndex].currentYearAmount = nonTotalsRows.reduce(
				(sum, row) => sum + Number(row.currentYearAmount),
				0
			);
			newRows[rowIndex].staffNumbers = nonTotalsRows.reduce(
				(sum, row) => sum + Number(row.staffNumbers),
				0
			);
		}

		return newRows;
	}, []);

	const mapQuotesToRows = (quotes: Quote[]) => {
		const newRows: RegionData[] = JSON.parse(JSON.stringify(initialRegionData.slice()));

		if ((quotes ?? []).length > 0) {
			(quotes[0].quoteRatingRegions ?? []).forEach((region) => {
				const regionName = Object.keys(regions).find(
					(key) => regions[key] === region.ratingRegionId
				);
				if (regionName) {
					const rowIndex = newRows.findIndex((r) => r.name === regionName);
					newRows[rowIndex].lastYearAmount = region.lastYearAmount;
					newRows[rowIndex].currentYearAmount = region.currentYearAmount;
					newRows[rowIndex].staffNumbers = region.staffNumbers;
				}
			});
		}
		// console.log('NEW ROWS', newRows, currentQuotes);
		setRatingRegionsRows(calculateTotalsAndPercentages(newRows));
	};

	useEffect(() => {
		mapQuotesToRows(currentQuotes);
	}, [currentQuotes]);

	/**
	 * Updates the decision tree based on the selected quote stored in local storage.
	 *
	 * The function first retrieves the "selectedQuote" from local storage and parses it.
	 * It then processes this quote, extracting the decision tree general questions and
	 * any related decision tree questions associated with quote products and quote packages.
	 *
	 * Once all relevant decision tree questions are accumulated, the function updates the
	 * related state with these questions.
	 *
	 * Assumptions:
	 * - The "selectedQuote" in local storage is expected to have properties like `decisionTreeGeneralQuestions`,
	 *   `quotes`, each `quote` may have `quoteProducts` and `quotePackages`.
	 * - Each `quoteProduct` and `quotePackage` may have associated `quoteProductDecisionTree` with
	 *   `decisionTreeQuestions`.
	 *
	 * @returns {void}
	 */
	useEffect(() => {
		let tempArr: DecisionTreeOrQuoteProduct[] = [];
		// console.log('QUOTE GROUP UPDATED for decision tree', quoteGroup);
		if (quoteGroup) {
			// setLoading(true);
			tempArr.push(quoteGroup.decisionTreeGeneralQuestions);

			quoteGroup.quotes.forEach((q) => {
				if (q?.quoteProducts) {
					q?.quoteProducts.forEach(
						(p) =>
							!!p?.quoteProductDecisionTree?.decisionTreeQuestions?.length &&
							tempArr.push(p)
					);
				}
				if (q?.quotePackages) {
					q?.quotePackages.forEach((qp) =>
						qp?.quoteProducts.forEach(
							(p) =>
								!!p?.quoteProductDecisionTree?.decisionTreeQuestions?.length &&
								tempArr.push(p)
						)
					);
				}
			});
			setDecisionTree(tempArr);
			// setLoading(false);
		}
	}, [quoteGroup]); //, quoteQuery.fulfilledTimeStamp]);

	const splitDecisionTree = useMemo(
		() =>
			decisionTree
				.map((tree) => {
					if ('quoteProductDecisionTree' in tree) {
						return tree;
					}
					const currentTree = tree as DecisionTree;
					const nonGSOFQuestions = currentTree.decisionTreeQuestions.filter(
						(q) => !regexGSOF.test(q.questionText)
					);
					return {
						...tree,
						decisionTreeQuestions: nonGSOFQuestions,
					};
				})
				.concat(
					decisionTree
						.filter((tree) => 'decisionTreeQuestions' in tree)
						.map((tree) => {
							const currentTree = tree as DecisionTree;
							const questions = currentTree.decisionTreeQuestions.filter((q) =>
								regexGSOF.test(q.questionText)
							);
							return {
								...tree,
								name: questions[0]?.name ?? 'General Statement Of Fact',
								decisionTreeQuestions: questions,
							};
						})
				),
		[decisionTree]
	);
	/**
	 * Effect hook to update the `setLoadingProducts` state.
	 *
	 * This effect is triggered whenever the `loading` state changes.
	 * It sets the value of the `setLoadingProducts` state to match
	 * the current value of the `loading` state.
	 * @effect
	 * @listens loading
	 */
	useEffect(() => {
		setLoadingProducts(loading || isQuoteLoading);
	}, [loading, isQuoteLoading]);

	return (
		<QuoteContext.Provider
			value={{
				decisionTree,
				splitDecisionTree,
				quoteGroup,
				currentQuotes,
				policyIds,
				needsReferral,
				hasReferralReason,
				willBeDeclined,
				isDeclined,
				isReferred,
				isQuoteInProgress,
				isQuotePolicyCreated,
				isReferralApproved,
                isReferralApprovedModifiable,
				isEveryQuoteInProgress,
				isQuoteBound,
				currentPremium,
				bindButtonEnabled,
				hasSanctions,
				allReferralReasons,
				setDecisionTree,
				calculateTotalsAndPercentages,
				mapQuotesToRows,
				// setCurrentQuotes,
				isQuoteDisabled,
				feeBreakdownRows,
				loadingProducts,
				setLoadingProducts,
				loading,
				setLoading,
				ratingRegionsRows,
				setRatingRegionsRows,
				quoteCheckbox,
				setQuoteCheckbox,
				minPolicyPeriod,
				maxPolicyPeriod,
				maxForwardDays,
				backdatingDays,
				minDate,
				minBackdatedDate,
				maxDate,
			}}
		>
			{children}
		</QuoteContext.Provider>
	);
};

// Custom hook to use the QuoteContext
const useQuoteContext = () => {
	const context = useContext(QuoteContext);
	if (!context) {
		throw new Error('useQuoteContext must be used within a QuoteProvider');
	}
	return context;
};

export { QuoteProvider, useQuoteContext };
